import { useState } from "react";
import { servicesData } from "../data/mock";
import { Badge } from "./ui/badge";
import {
  Thermometer, Wrench, Cpu, Wind, Fan, Gauge,
  PenTool, Calculator, Zap, Droplets, AirVent, Pipette,
  Hammer, Building2, MonitorSpeaker, Settings, Cog, CircuitBoard
} from "lucide-react";

const serviceIcons = {
  "Split AC Installation & Servicing": Wrench,
  "Window AC Repair": Settings,
  "Tower AC Systems": MonitorSpeaker,
  "Annual Maintenance Contracts (AMC)": Gauge,
  "Gas Charging & PCB Repair": CircuitBoard,
  "Compressor Diagnostics": Cpu,
  "Cassette AC Systems": AirVent,
  "Ductable AC Systems": Wind,
  "VRF / VRV Systems": Thermometer,
  "Centralized Air-Conditioning": Fan,
  "Air Distribution Systems": Wind,
  "Ventilation Systems": AirVent,
  "HVAC Design & Layout Planning": PenTool,
  "Heat Load Calculations": Calculator,
  "SLD & Electrical Coordination": Zap,
  "Chilled Water Systems": Droplets,
  "AHU & FCU Systems": Fan,
  "Copper Piping & Insulation": Pipette,
  "Duct Fabrication & Installation": Hammer,
  "HVAC Execution for Malls & Complexes": Building2,
};

const tabs = [
  { key: "residential", label: "Residential", sublabel: "& Basic" },
  { key: "commercial", label: "Commercial", sublabel: "Systems" },
  { key: "advanced", label: "Advanced", sublabel: "& Large-Scale" },
];

export const ServicesSection = () => {
  const [activeTab, setActiveTab] = useState("residential");
  const data = servicesData[activeTab];

  return (
    <section id="services" className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-brand-50 rounded-full px-4 py-1.5 mb-5 border border-brand-100">
            <Cog className="w-4 h-4 text-brand-600" />
            <span className="text-sm font-medium text-brand-700">HVAC Services</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            End-to-End HVAC Solutions Across India
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto text-base">
            Comprehensive HVAC services — from precision-engineered VRF/VRV systems to large-scale centralized air conditioning for commercial complexes, retail spaces &amp; industrial facilities nationwide.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white rounded-xl p-1.5 shadow-sm border border-slate-200">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`relative px-5 sm:px-7 py-2.5 rounded-lg text-sm font-medium transition-all duration-250 ${
                  activeTab === tab.key
                    ? "bg-brand-600 text-white shadow-md"
                    : "text-slate-600 hover:text-brand-600 hover:bg-brand-50"
                }`}
              >
                <span className="hidden sm:inline">{tab.label} {tab.sublabel}</span>
                <span className="sm:hidden">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Category Info */}
        <div className="text-center mb-10">
          <h3 className="text-xl font-semibold text-slate-800 mb-2">{data.title}</h3>
          <p className="text-slate-500 text-sm">{data.description}</p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.services.map((service, index) => {
            const IconComp = serviceIcons[service.name] || Wrench;
            return (
              <div
                key={service.name}
                className="group bg-white rounded-xl p-6 border border-slate-100 hover:border-brand-200 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                style={{ animationDelay: `${index * 60}ms` }}
              >
                <div className="w-11 h-11 rounded-xl bg-brand-50 group-hover:bg-brand-100 flex items-center justify-center mb-4 transition-colors duration-300">
                  <IconComp className="w-5 h-5 text-brand-600" />
                </div>
                <h4 className="text-base font-semibold text-slate-800 mb-2 group-hover:text-brand-700 transition-colors">
                  {service.name}
                </h4>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {service.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Bottom Badge */}
        <div className="text-center mt-12">
          <Badge variant="outline" className="text-brand-600 border-brand-200 bg-brand-50/50 px-5 py-2 text-sm">
            Design → Supply → Installation → Testing → Commissioning → Maintenance
          </Badge>
        </div>
      </div>
    </section>
  );
};
